import { ICartEntry, IMinimalCartEntry, MinimalCartEntryType } from 'features/cart/types';
import {
  findRewardBenefitFromHydratedCartEntry,
  getCartEntryId,
  getHydratedEntityId,
} from 'features/cart/utils/minimal-cart';
import { ModifierType } from 'features/menu/components/product-detail/modifier/types';
import {
  getModifierMultiplierPrice,
  mapModifier,
} from 'features/menu/components/product-detail/modifier/utils';
import { ComboSlotType } from 'features/menu/components/product-detail/product-customization/types';
import {
  getComboSlotType,
  isCustomizableEntry,
} from 'features/menu/components/product-detail/product-customization/utils';
import { isCustomization } from 'features/menu/components/product-detail/utils';
import {
  DEFAULT_SLOT_KEY,
  FlowStep,
  ModifiersSelections,
  PickerSelections,
  ProductWizardStep,
  SlotSelections,
  SlotsSelections,
  WizardRouteRedirectTarget,
} from 'features/menu/components/product-wizard/types';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { EntryTypeEnum, IPriceDto } from 'generated/graphql-gateway';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import {
  getComboSlotAvailableEntries,
  getEntryPriceOrZero,
  getFirstDefaultId,
  getPickerProduct,
  isCombo,
  isEntryAvailable,
  isItem,
  isPicker,
} from 'state/unified-menu/utils';
import { isCartEntryComboSlot } from 'utils/cart/is-cart-type';
import { CartEntryType } from 'utils/cart/types';
import { logger } from 'utils/logger';

export function computeSelectedProduct({
  menu: indexedMenu,
  menuObject,
  posServiceMode,
  editingCartEntry,
  isMinimalCartUIEnabled,
  pickerSelections,
  pickerAspectKey,
}: {
  menu: IndexedMenu<ILocalizedMenuEntry>;
  menuObject: ILocalizedMenuEntry;
  posServiceMode: PosServiceMode;
  editingCartEntry?: IMinimalCartEntry | ILegacyCartEntry;
  isMinimalCartUIEnabled: boolean;
  pickerSelections?: Record<string, string> | undefined;
  pickerAspectKey?: string;
}) {
  if (!editingCartEntry) {
    return initializeSelectedProduct({
      menu: indexedMenu,
      item: menuObject,
      posServiceMode,
      pickerSelections,
      pickerAspectKey,
    });
  }

  return isMinimalCartUIEnabled
    ? computeSelectedProductFromMinimalCartEntry({
        menu: indexedMenu,
        editingCartEntry: editingCartEntry as IMinimalCartEntry,
      })
    : computeSelectedProductFromLegacyCartEntry({
        menu: indexedMenu,
        item: menuObject,
        editingCartEntry: editingCartEntry as ILegacyCartEntry,
      });
}

export function initializeSelectedProduct({
  menu,
  item,
  posServiceMode,
  pickerSelections,
  pickerAspectKey,
}: {
  menu: IndexedMenu<ILocalizedMenuEntry>;
  item: ILocalizedMenuEntry;
  posServiceMode: PosServiceMode;
  pickerSelections?: Record<string, string> | undefined;
  pickerAspectKey?: string;
}): ILocalizedMenuEntry {
  if (isEntryAvailable(item, posServiceMode) && (isItem(item) || isCombo(item))) {
    return item;
  }

  return getPickerProduct(menu, item, pickerSelections ?? {}, posServiceMode, pickerAspectKey);
}

export function computeSelectedProductFromMinimalCartEntry({
  menu,
  editingCartEntry,
}: {
  menu: IndexedMenu<ILocalizedMenuEntry>;
  editingCartEntry?: IMinimalCartEntry;
}) {
  let editingProduct = editingCartEntry?.id ? menu.find(editingCartEntry.id) : undefined;

  if (editingProduct && editingProduct.rewardIdReference) {
    const fallbackId = editingCartEntry?.menuSelections?.[0]?.id;
    editingProduct = fallbackId ? menu.find(fallbackId) : undefined;
  }

  if (editingProduct && (isCombo(editingProduct) || isItem(editingProduct))) {
    return editingProduct;
  }
}

/**
 * @deprecated use computeSelectedProductFromCartEntry instead
 */
export function computeSelectedProductFromLegacyCartEntry({
  menu,
  item,
  editingCartEntry,
}: {
  menu: IndexedMenu<ILocalizedMenuEntry>;
  item: ILocalizedMenuEntry;
  editingCartEntry?: ILegacyCartEntry;
}) {
  const editingProduct = editingCartEntry ? menu.find(getCartEntryId(editingCartEntry)) : undefined;

  // @TODO: we should consider updating this in the future for picker rewards
  if (editingCartEntry?.type === CartEntryType.offerCombo) {
    return item;
  }

  if (editingProduct && (isCombo(editingProduct) || isItem(editingProduct))) {
    return editingProduct;
  }
}

export function initializeSlotSelections(
  selectedProduct: ILocalizedMenuEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  isComboFlow: boolean,
  posServiceMode: PosServiceMode,
  activeDayParts?: IDayPartBoundary[],
  storeHasBurgersForBreakfast?: boolean
): SlotsSelections {
  const slotSelections: SlotsSelections = {};

  if (isItem(selectedProduct)) {
    slotSelections[DEFAULT_SLOT_KEY] = {
      itemId: selectedProduct.id,
      modifiers: initializeSlotModifiers(
        menu,
        selectedProduct.id,
        posServiceMode,
        true,
        isComboFlow
      ),
    };
  } else if (isCombo(selectedProduct)) {
    for (const comboSlotRef of selectedProduct.options?.entries ?? []) {
      const comboSlot = menu.get(comboSlotRef.entryId, { type: EntryTypeEnum.COMBO_SLOT });
      const comboSlotAvailableEntries = getComboSlotAvailableEntries({
        menu,
        posServiceMode,
        entries: comboSlot.options?.entries,
        activeDayParts: activeDayParts ?? [],
        storeHasBurgersForBreakfast,
      });

      const singleOption = comboSlotAvailableEntries.length === 1;

      const slotOptions = comboSlot.options?.entries ?? [];
      const hasSingleAvailableOption = slotOptions.length === 2 && singleOption;

      if (singleOption || isComboFlow) {
        let itemId: string;

        if (hasSingleAvailableOption) {
          itemId = comboSlotAvailableEntries[0].id;
        } else {
          itemId = getFirstDefaultId(comboSlot);
        }

        slotSelections[comboSlot.id] = {
          itemId,
          modifiers: initializeSlotModifiers(menu, itemId, posServiceMode, true, isComboFlow),
        };
      }
    }
  }

  return slotSelections;
}

export function initializeSlotSelectionsFromMinimalCartEntry(
  selectedProduct: ILocalizedMenuEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  editingCartEntry: IMinimalCartEntry
): SlotsSelections {
  const slotSelections: SlotsSelections = {};

  editingCartEntry = unwrapLoyaltyBenefitEntry(editingCartEntry) ?? editingCartEntry;
  if (isItem(selectedProduct)) {
    slotSelections[DEFAULT_SLOT_KEY] = {
      itemId: selectedProduct.id,
      modifiers: getModifiersSelectionsFromMinimalCartEntry(menu, editingCartEntry),
    };
    return slotSelections;
  }
  if (!isCombo(selectedProduct)) {
    return slotSelections;
  }

  for (const comboSlotRef of selectedProduct.options?.entries ?? []) {
    const comboSlot = menu.get(comboSlotRef.entryId, { type: EntryTypeEnum.COMBO_SLOT });
    const isSingleOption = comboSlot.options?.entries?.length === 1;
    if (isSingleOption) {
      const itemId = getFirstDefaultId(comboSlot);
      const itemCartEntry = findItemInMinimalCartEntries(menu, editingCartEntry, itemId);

      slotSelections[comboSlot.id] = {
        itemId,
        modifiers: itemCartEntry
          ? getModifiersSelectionsFromMinimalCartEntry(menu, itemCartEntry)
          : {},
      };
    }

    const editingSlot = editingCartEntry.menuSelections?.find(child =>
      comboSlot.options?.entries?.some(entry => entry.entryId === child.id)
    );

    if (editingSlot) {
      slotSelections[comboSlot.id] = {
        itemId: editingSlot.id,
        modifiers: getModifiersSelectionsFromMinimalCartEntry(menu, editingSlot),
      };
    }
  }

  return slotSelections;
}

/**
 * Initialize the slot selections from a legacy cart entry.
 * @deprecated use initializeSlotSelectionsFromCartEntry instead
 */
export function initializeSlotSelectionsFromLegacyCartEntry(
  selectedProduct: ILocalizedMenuEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  editingCartEntry: ILegacyCartEntry
): SlotsSelections {
  const slotSelections: SlotsSelections = {};

  if (isItem(selectedProduct)) {
    let itemCartEntry = editingCartEntry;
    const rewardBenefit = findRewardBenefitFromHydratedCartEntry(editingCartEntry);
    if (rewardBenefit) {
      itemCartEntry = rewardBenefit;
    }

    slotSelections[DEFAULT_SLOT_KEY] = {
      itemId: selectedProduct.id,
      modifiers: getModifiersSelectionsFromLegacyCartEntry(itemCartEntry),
    };
    return slotSelections;
  }

  if (!isCombo(selectedProduct)) {
    return slotSelections;
  }

  for (const comboSlotRef of selectedProduct.options?.entries ?? []) {
    const comboSlot = menu.get(comboSlotRef.entryId, { type: EntryTypeEnum.COMBO_SLOT });
    const isSingleOption = comboSlot.options?.entries?.length === 1;
    if (isSingleOption) {
      const itemId = getFirstDefaultId(comboSlot);
      const itemCartEntry = findItemInLegacyCartEntries(editingCartEntry, comboSlot.id, itemId);
      slotSelections[comboSlot.id] = {
        itemId,
        modifiers: itemCartEntry ? getModifiersSelectionsFromLegacyCartEntry(itemCartEntry) : {},
      };
    } else {
      const editingSlot = editingCartEntry.children.find(
        cartEntry =>
          isCartEntryComboSlot(cartEntry) && comboSlot.id.endsWith(getCartEntryId(cartEntry))
      );
      if (editingSlot) {
        slotSelections[comboSlot.id] = {
          itemId: getCartEntryId(editingSlot.children[0]),
          modifiers: getModifiersSelectionsFromLegacyCartEntry(editingSlot.children[0]),
        };
      }
    }
  }

  return slotSelections;
}

/**
 * Initialize the slot modifiers for a given item in the menu.
 *
 * Cases handled:
 * - Picker Meals with modifiers upcharges (Whopper with Bacon)
 * - Offers with required modifiers ($30 Ultimate Bundle - the slot with 16pc chicken nuggets has a required dipping sauce)
 * - Switching between items in a combo (Whopper Meals Sides that have dipping sauces)
 *
 */
export function initializeSlotModifiers(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  itemId: string,
  posServiceMode: PosServiceMode,
  onlyUpcharges = false,
  isComboFlow: boolean
): ModifiersSelections {
  const modifiersSelections: ModifiersSelections = {};

  const item = menu.get(itemId);
  const modifiersCount = item.options?.entries?.length ?? 0;
  for (const { entryId: modifierId, quantity: modifierQuantity } of item.options?.entries ?? []) {
    const modifier = menu.get(modifierId);
    const defaultMultiplierId = getFirstDefaultId(modifier);

    const isUpcharge =
      getModifierMultiplierPrice(modifier, defaultMultiplierId, posServiceMode) > 0;

    const requiredQuantity = modifierQuantity?.def ?? modifierQuantity?.min ?? 0;
    const available = !(modifier.availability && modifier.availability[posServiceMode] === false);

    const mappedModifier = mapModifier(menu, modifierId, modifiersCount, posServiceMode);

    const modifierNotCheckboxAndNotNonNumeric =
      mappedModifier?.type !== ModifierType.CHECKBOX &&
      mappedModifier?.type !== ModifierType.NON_NUMERIC;

    const isRequiredModifier =
      isComboFlow && available && requiredQuantity > 0 && modifierNotCheckboxAndNotNonNumeric;

    if (onlyUpcharges && !isUpcharge && !isRequiredModifier) {
      continue;
    }

    const multiplierRef = modifier.options?.entries?.find(
      x =>
        x.entryId === defaultMultiplierId &&
        !(x.availability && x.availability[posServiceMode] === false)
    );

    if (multiplierRef) {
      modifiersSelections[modifier.id] = {};
      modifiersSelections[modifier.id][defaultMultiplierId] = requiredQuantity ?? 1;
    }
  }

  return modifiersSelections;
}

function findItemInMinimalCartEntries(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  cartEntry: IMinimalCartEntry,
  itemId: string
): IMinimalCartEntry | undefined {
  const menuEntry = menu.get(cartEntry.id);
  if (isItem(menuEntry)) {
    return cartEntry;
  }
  return cartEntry.menuSelections?.find(item => item.id === itemId);
}

function unwrapLoyaltyBenefitEntry(
  editingCartEntry?: IMinimalCartEntry
): IMinimalCartEntry | undefined {
  const maybeLoyaltyEntry = editingCartEntry as ICartEntry | undefined;
  const isWrappedIncentiveCombo =
    maybeLoyaltyEntry?.type === MinimalCartEntryType.incentive &&
    maybeLoyaltyEntry.children?.[0]?.type === MinimalCartEntryType.combo;

  if (maybeLoyaltyEntry?.type === MinimalCartEntryType.reward || isWrappedIncentiveCombo) {
    const benefitEntry = maybeLoyaltyEntry.menuSelections?.[0];
    if (benefitEntry) {
      return benefitEntry;
    }
  }

  return editingCartEntry;
}

/**
 * @deprecated use findItemInCartEntries instead
 */
function findItemInLegacyCartEntries(
  cartEntry: ILegacyCartEntry,
  comboSlotId: string,
  itemId: string
): ILegacyCartEntry | undefined {
  const matchingDirectItem = cartEntry.children.find(x => getCartEntryId(x) === itemId);
  if (matchingDirectItem) {
    return matchingDirectItem;
  }
  const matchingComboSlot = cartEntry.children.find(x => comboSlotId.endsWith(getCartEntryId(x)));

  return matchingComboSlot?.children.find(x => getCartEntryId(x) === itemId);
}

function getModifiersSelectionsFromMinimalCartEntry(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  cartEntry: IMinimalCartEntry
): ModifiersSelections {
  const modifiersSelections: ModifiersSelections = {};
  const menuEntry = menu.get(cartEntry.id);
  if (!isItem(menuEntry)) {
    logger.error({
      message: '[getModifiersSelectionsFromMinimalCartEntry]: cartEntry is not an item',
      context: { cartEntry, menuEntry },
    });
    return {};
  }
  for (const modifier of cartEntry.menuSelections || []) {
    const parts = modifier.id.split('$');
    // this result may be in the form of $item$modifierGroupId$multiplierId
    const modifierGroupId =
      parts.length > 1
        ? parts.slice(0, 2).join('$')
        : menuEntry.options?.entries?.find(modifierRef => {
            const modifierEntry = menu.find(modifierRef.entryId);
            return modifierEntry?.options?.entries?.some(entry => entry.entryId === modifier.id);
          })?.entryId;

    if (!modifierGroupId) {
      continue;
    }

    if (!modifiersSelections[modifierGroupId]) {
      modifiersSelections[modifierGroupId] = {};
    }
    modifiersSelections[modifierGroupId][modifier.id] = modifier.quantity;
  }

  return modifiersSelections;
}

/**
 * @deprecated use getModifiersSelectionsFromCartEntry instead
 */
function getModifiersSelectionsFromLegacyCartEntry(
  itemCartEntry: ILegacyCartEntry
): ModifiersSelections {
  const modifiersSelections: ModifiersSelections = {};

  itemCartEntry.children.forEach(modifierCartEntry => {
    modifierCartEntry.children.forEach(modifierMultiplierCartEntry => {
      const modifierMultiplierKey = getCartEntryId(modifierMultiplierCartEntry);
      if (modifierMultiplierKey) {
        const slotKey = getHydratedEntityId(
          getCartEntryId(modifierCartEntry),
          getCartEntryId(itemCartEntry)
        );
        if (!modifiersSelections[slotKey]) {
          modifiersSelections[slotKey] = {};
        }
        modifiersSelections[slotKey][modifierMultiplierKey] = modifierMultiplierCartEntry.quantity;
      }
    });
  });

  return modifiersSelections;
}

// function computeComboSlotCalories(
//   menu: IndexedMenu,
//   comboSlot: MenuEntry,
//   selection: SlotSelections | undefined
// ): number {
//   const entry = selection
//     ? menu.get(selection.itemId)
//     : menu.get(getFirstDefaultId(comboSlot, true));

//   const calories = entry.nutrition?.calories?.def ?? 0;
//   return calories * (entry.options?.quantity?.default ?? 0);
// }

// export function computeComboCalories(
//   menu: IndexedMenu,
//   combo: MenuEntry,
//   selection: SlotsSelections
// ) {
//   let calories = 0;
//   for (const comboSlotRef of combo.options?.entries ?? []) {
//     calories += computeComboSlotCalories(
//       menu,
//       menu.get(comboSlotRef.entryId),
//       selection[comboSlotRef.entryId]
//     );
//   }
//   return calories;
// }

function computeComboSlotSodium(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  comboSlot: ILocalizedMenuEntry,
  selection: SlotSelections | undefined
): number {
  const entry = selection
    ? menu.get(selection.itemId)
    : menu.get(getFirstDefaultId(comboSlot, true));

  return entry.nutrition?.nutrients?.sodium ?? 0;
}

export function computeComboSodium(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  combo: ILocalizedMenuEntry,
  selection: SlotsSelections
): number {
  let sodium = 0;
  for (const comboSlotRef of combo.options?.entries ?? []) {
    sodium += computeComboSlotSodium(
      menu,
      menu.get(comboSlotRef.entryId),
      selection[comboSlotRef.entryId]
    );
  }

  return sodium;
}

export function computeMenuItemPrice(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  entry: ILocalizedMenuEntry,
  serviceMode: PosServiceMode,
  itemContextualPrice: IPriceDto | undefined,
  selections: ModifiersSelections
): number {
  if (!isItem(entry)) {
    throw new Error(`Entry ${entry.id} is not an item`);
  }

  let price = getEntryPriceOrZero(entry, serviceMode, itemContextualPrice);

  for (const [modifierKey, multipliers] of Object.entries(selections)) {
    const modifier = menu.get(modifierKey);
    for (const [multiplierId, quantity] of Object.entries(multipliers)) {
      price += getModifierMultiplierPrice(modifier, multiplierId, serviceMode) * quantity;
    }
  }

  return price;
}

export function buildFlow(
  menuObject: ILocalizedMenuEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  pickerSelections: PickerSelections,
  posServiceMode: PosServiceMode,
  activeDayParts?: IDayPartBoundary[],
  storeHasBurgersForBreakfast?: boolean
): FlowStep[] {
  const flow: FlowStep[] = [];

  if (menuObject.type !== EntryTypeEnum.PICKER) {
    return flow;
  }

  // Step 1: Add the ProductHome step
  const firstPickerAspectId = getFirstDefaultId(menuObject);
  flow.push({
    step: ProductWizardStep.ProductHome,
    pickerAspectKey: firstPickerAspectId,
  });

  // Step 2: Traverse Picker Aspects
  let currentPickerAspectId = firstPickerAspectId;
  let selectedProduct;
  while (currentPickerAspectId) {
    let selectedAspectOptionId = pickerSelections[currentPickerAspectId];
    if (!selectedAspectOptionId) {
      selectedAspectOptionId = getFirstDefaultId(
        menu.get(currentPickerAspectId, { type: EntryTypeEnum.PICKER_ASPECT })
      );
    }
    const selectedAspectOption = menu.get(selectedAspectOptionId, {
      type: EntryTypeEnum.ASPECT_OPTION,
    });
    const nextEntityId = getFirstDefaultId(selectedAspectOption);
    const nextEntity = menu.get(nextEntityId);
    if (nextEntity.type !== EntryTypeEnum.PICKER_ASPECT) {
      selectedProduct = nextEntity;
      break;
    }

    flow.push({
      step: ProductWizardStep.PickerSelection,
      pickerAspectKey: nextEntityId,
    });
    currentPickerAspectId = nextEntityId;
  }

  // Step 3.A: When Combo => Traverse Combo Slots
  if (selectedProduct && isCombo(selectedProduct)) {
    for (const slot of selectedProduct.options?.entries || []) {
      const comboSlotMenuEntry = menu.get(slot.entryId, { type: EntryTypeEnum.COMBO_SLOT });
      if (
        !isCustomizableEntry(
          comboSlotMenuEntry,
          menu,
          posServiceMode,
          activeDayParts,
          !!storeHasBurgersForBreakfast
        )
      ) {
        continue;
      }

      const comboSlotType: ComboSlotType = getComboSlotType(menu, slot.entryId!);
      const step: ProductWizardStep =
        comboSlotType === ComboSlotType.SIDES
          ? ProductWizardStep.PickerCustomizationSides
          : comboSlotType === ComboSlotType.DRINKS
          ? ProductWizardStep.PickerCustomizationDrinks
          : ProductWizardStep.PickerCustomization;
      flow.push({ step, slotKey: slot.entryId });
    }
  }

  // Step 3.B: When Item => Add mainItem customization
  if (
    selectedProduct &&
    isItem(selectedProduct) &&
    isCustomizableEntry(
      selectedProduct,
      menu,
      posServiceMode,
      activeDayParts,
      !!storeHasBurgersForBreakfast
    )
  ) {
    flow.push({
      step: ProductWizardStep.PickerCustomization,
      slotKey: DEFAULT_SLOT_KEY,
    });
  }

  // Step 4: Add the PickerReview step
  flow.push({
    step: ProductWizardStep.PickerReview,
  });

  // Nullify invalid flows (only Home and Review steps)
  if (flow.length === 2) {
    return [];
  }

  return flow;
}

/**
 * Maps URL-driven wizard params to an index in the built {@link flow}.
 *
 * ProductHome always resolves to index 0 so the first picker aspect comes from
 * {@link buildFlow}, not a potentially stale or malformed `pickerAspectKey` in the URL.
 *
 * For other steps, tries an exact match on `step`, `slotKey`, and `pickerAspectKey`.
 * When that fails (e.g. deeplink with an aspect-option id), falls back to matching
 * only `step` and `slotKey` so the correct flow step can still be found.
 *
 * @returns The flow index, or -1 when no step matches.
 */
export function findCurrentStepIndex(flow: FlowStep[], urlStep: FlowStep): number {
  if (urlStep.step === ProductWizardStep.ProductHome) {
    return 0;
  }

  // Match URL params to a flow step. step must always match; slotKey and pickerAspectKey
  // only constrain the match when the flow step defines them (combo slots / picker aspects).
  const exactMatch = flow.findIndex(
    x =>
      x.step === urlStep.step &&
      (!x.slotKey || x.slotKey === urlStep.slotKey) &&
      (!x.pickerAspectKey || x.pickerAspectKey === urlStep.pickerAspectKey)
  );

  // URL params align with a flow step — use it as-is (normal in-app navigation).
  if (exactMatch >= 0) {
    return exactMatch;
  }

  // pickerAspectKey may be stale or wrong (e.g. deeplink); match by step + slotKey only.
  return flow.findIndex(
    x => x.step === urlStep.step && (!x.slotKey || x.slotKey === urlStep.slotKey)
  );
}

/**
 * Builds the active wizard step by combining URL params with the built {@link flow}.
 *
 * Used by {@link useProductWizardFlow} so navigation state (`step`, `slotKey`) still
 * comes from the URL while `pickerAspectKey` is taken from {@link findCurrentStepIndex}'s
 * matched flow step whenever possible — avoiding stale or malformed deeplink values.
 *
 * - `step` always reflects the URL.
 * - `slotKey` and `pickerAspectKey` prefer the matched flow step; URL values are
 *   only used as fallback when the flow cannot supply them.
 * - `currentStepIndex` defaults to 0 when no flow step matches.
 */
export function resolveCurrentFlowStep(
  flow: FlowStep[],
  urlStep: FlowStep
): { currentFlowStep: FlowStep; currentStepIndex: number } {
  const currentStepIndex = findCurrentStepIndex(flow, urlStep);
  const flowStep = currentStepIndex >= 0 ? flow[currentStepIndex] : undefined;

  return {
    // Default to the first flow step when URL params don't match any step (avoids -1 breaking navigation).
    currentStepIndex: currentStepIndex >= 0 ? currentStepIndex : 0,
    currentFlowStep: {
      step: urlStep.step,
      slotKey: urlStep.slotKey || flowStep?.slotKey,
      pickerAspectKey: flowStep?.pickerAspectKey ?? urlStep.pickerAspectKey,
    },
  };
}

export interface IGetWizardRouteRedirectTargetParams {
  urlStep: FlowStep;
  flow: FlowStep[];
  menuObject: ILocalizedMenuEntry;
  selectedProduct: ILocalizedMenuEntry;
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>;
  hasInAppNavigation: boolean;
  cartIdEditing: string;
  isProductReviewFromCart: boolean;
  isAuthenticated: boolean;
  hasCartEntries: boolean;
}

/**
 * Resolves where to send the user when the URL contains an invalid slotKey.
 * Cart-edit sessions (`cartIdEditing`) always leave the PDP (cart or menu).
 * Browse/add sessions reset to product home.
 */
export function getInvalidSlotKeyRedirectTarget(
  cartIdEditing: string,
  isAuthenticated: boolean,
  hasCartEntries: boolean
): WizardRouteRedirectTarget {
  if (cartIdEditing) {
    if (isAuthenticated && hasCartEntries) {
      return WizardRouteRedirectTarget.Cart;
    }

    return WizardRouteRedirectTarget.Menu;
  }

  return WizardRouteRedirectTarget.Home;
}

/**
 * Returns whether a slotKey is valid for a customization screen.
 * Uses indexedMenu.find (never get) so validation cannot throw.
 */
export function isValidCustomizationSlotKey(
  slotKey: string,
  selectedProduct: ILocalizedMenuEntry,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>
): boolean {
  if (slotKey === DEFAULT_SLOT_KEY) {
    return isItem(selectedProduct);
  }

  if (!slotKey || !isCombo(selectedProduct)) {
    return false;
  }

  return (selectedProduct.options?.entries ?? []).some(ref => {
    if (ref.entryId === slotKey) {
      return indexedMenu.find(ref.entryId, { type: EntryTypeEnum.COMBO_SLOT }) !== undefined;
    }

    const comboSlot = indexedMenu.find(ref.entryId, { type: EntryTypeEnum.COMBO_SLOT });
    return comboSlot?.id === slotKey;
  });
}

/**
 * Picker products may only be opened directly via URL on ProductHome or PickerReview when editing from cart.
 */
export function isAllowedPickerUrlEntry(
  step: ProductWizardStep,
  cartIdEditing: string,
  isProductReviewFromCart: boolean
): boolean {
  if (step === ProductWizardStep.ProductHome) {
    return true;
  }

  if (step === ProductWizardStep.PickerReview && cartIdEditing && isProductReviewFromCart) {
    return true;
  }

  return false;
}

function hasInvalidCustomizationSlotKey(
  slotKey: string | undefined,
  selectedProduct: ILocalizedMenuEntry,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>
): boolean {
  if (!slotKey) {
    return false;
  }

  return !isValidCustomizationSlotKey(slotKey, selectedProduct, indexedMenu);
}

/** PickerReview → goToCustomization uses ProductCustomization, which is outside the built flow. */
function isAllowedPickerReviewSlotCustomization(
  menuObject: ILocalizedMenuEntry,
  step: ProductWizardStep,
  slotKey: string | undefined
): boolean {
  return (
    isPicker(menuObject) && step === ProductWizardStep.ProductCustomization && Boolean(slotKey)
  );
}

/** Picker mid-flow steps reached via URL deeplink without in-app navigation. */
function shouldRedirectPickerMidFlowDeeplink(
  menuObject: ILocalizedMenuEntry,
  flow: FlowStep[],
  urlStep: FlowStep,
  step: ProductWizardStep,
  hasInAppNavigation: boolean,
  cartIdEditing: string,
  isProductReviewFromCart: boolean
): boolean {
  if (!isPicker(menuObject) || flow.length === 0) {
    return false;
  }

  if (
    !hasInAppNavigation &&
    !isAllowedPickerUrlEntry(step, cartIdEditing, isProductReviewFromCart)
  ) {
    return true;
  }

  return (
    isCustomization(step) &&
    step !== ProductWizardStep.ProductCustomization &&
    findCurrentStepIndex(flow, urlStep) === -1
  );
}

/**
 * Determines whether the current URL should redirect to product home or the main menu.
 * Prevents IndexedMenu.get throws / Bummer screen from invalid slotKey or picker deeplinks.
 */
export function getWizardRouteRedirectTarget({
  urlStep,
  flow,
  menuObject,
  selectedProduct,
  indexedMenu,
  hasInAppNavigation,
  cartIdEditing,
  isProductReviewFromCart,
  isAuthenticated,
  hasCartEntries,
}: IGetWizardRouteRedirectTargetParams): WizardRouteRedirectTarget | null {
  if (!menuObject?.id || !selectedProduct?.id) {
    return WizardRouteRedirectTarget.Menu;
  }

  const step = urlStep.step;
  const matchedFlowStep = flow[findCurrentStepIndex(flow, urlStep)];
  const slotKey = urlStep.slotKey || matchedFlowStep?.slotKey || '';

  const redirectInvalidSlotKey = () =>
    getInvalidSlotKeyRedirectTarget(cartIdEditing, isAuthenticated, hasCartEntries);

  // Invalid slotKey in the URL or on a customization step.
  if (
    hasInvalidCustomizationSlotKey(urlStep.slotKey, selectedProduct, indexedMenu) ||
    (isCustomization(step) && hasInvalidCustomizationSlotKey(slotKey, selectedProduct, indexedMenu))
  ) {
    return redirectInvalidSlotKey();
  }

  // Valid entry points — no redirect.
  if (isAllowedPickerReviewSlotCustomization(menuObject, step, urlStep.slotKey)) {
    return null;
  }

  // Picker direct URL entry: ProductHome, or PickerReview when editing from cart.
  if (
    !isCustomization(step) &&
    isAllowedPickerUrlEntry(step, cartIdEditing, isProductReviewFromCart)
  ) {
    return null;
  }

  if (
    shouldRedirectPickerMidFlowDeeplink(
      menuObject,
      flow,
      urlStep,
      step,
      hasInAppNavigation,
      cartIdEditing,
      isProductReviewFromCart
    )
  ) {
    return getInvalidSlotKeyRedirectTarget(cartIdEditing, isAuthenticated, hasCartEntries);
  }

  return null;
}
